import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { productPages } from '../data/kit';
export function ProductPreview() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const updateActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    let minDist = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const el = child as HTMLElement;
      const dist = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    setActive(closest);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActive);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [updateActive]);
  const goTo = (i: number) => {
    const track = trackRef.current;
    const target = track?.children[i] as HTMLElement | undefined;
    if (!track || !target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: target.offsetLeft - (track.clientWidth - target.offsetWidth) / 2,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const last = productPages.length - 1;
  return <section className="bg-celeste-100 pt-10 pb-12 md:pb-16" aria-labelledby="producto-titulo" data-id="element-214" data-mp="13nf7qo6595x4">
      <div className="max-w-6xl mx-auto px-4" data-id="element-215" data-mp="ypdr09w6xiot">
        <div className="text-center max-w-2xl mx-auto" data-id="element-216" data-mp="1w5110nfwnjts">
          <h2 id="producto-titulo" className="font-serif text-[28px] md:text-5xl font-bold text-wine tracking-tight leading-[1.1]" data-id="element-217" data-mp="1w6ucqh5dku8n">
            MIRÁ TODO LO QUE VAS A RECIBIR
          </h2>
          <p className="text-ink-muted text-[15px] md:text-lg leading-snug mt-3" data-id="element-218" data-mp="2ad5pm2tr62kf">
            Conocé por dentro los módulos, actividades y recursos que vas a
            tener para acompañar su lectura durante los próximos 30 días.
          </p>
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto mt-8" data-id="element-219" data-mp="25ijnyrdh3odj">
        <div ref={trackRef} className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-[8%] sm:px-[25%] lg:px-[35%] pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="region" aria-roledescription="carrusel" aria-label="Páginas del Kit" data-id="element-220" data-mp="1rjsan76fq6qg">
          {productPages.map((page, i) => <figure key={page.src} className="snap-center shrink-0 w-[84%] sm:w-[50%] lg:w-[30%]" aria-roledescription="página" aria-label={`${i + 1} de ${productPages.length}`} data-id="element-221" data-mp="3cxob27err5oi" data-mpi={i}>
              <div className={`rounded-2xl overflow-hidden bg-cream-50 ring-1 ring-celeste-200 shadow-[0_24px_50px_-24px_rgba(34,56,107,0.4)] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] ${active === i ? 'opacity-100 scale-100' : 'opacity-60 scale-[0.96]'}`} data-id="element-222" data-mp="15cpjpnpa8vg6">
                <img src={page.src} alt={page.alt} className="w-full h-auto aspect-[804/1134] object-cover object-top" width={804} height={1134} loading={i < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} data-id="element-223" data-mp="1ozduzaxfph0a" />
              </div>
              <figcaption className="text-center mt-3" data-id="element-224" data-mp="3mauostk09be">
                <span className="text-[11px] font-extrabold tracking-[0.2em] uppercase text-rose-600" data-id="element-225" data-mp="3msw03v2pdq6">
                  {page.tag}
                </span>
                <span className="block font-serif text-wine font-semibold text-base sm:text-lg mt-0.5" data-id="element-226" data-mp="12w9gvdfum3p">
                  {page.title}
                </span>
                <span className="block text-ink-muted text-[13px] sm:text-sm leading-snug mt-1 max-w-[260px] mx-auto" data-id="element-227" data-mp="55kjh6dw1z29">
                  {page.desc}
                </span>
              </figcaption>
            </figure>)}
        </div>

        <button type="button" onClick={() => goTo(Math.max(0, active - 1))} disabled={active === 0} aria-label="Página anterior" className="hidden sm:flex absolute left-4 lg:left-8 top-[42%] -translate-y-1/2 w-11 h-11 rounded-full bg-white text-wine items-center justify-center shadow-lg transition-opacity duration-150 ease-out hover:bg-rose-50 disabled:opacity-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300" data-id="element-228" data-mp="3dkb04nsx5xr9">
          <ChevronLeft className="w-5 h-5" data-id="element-229" data-mp="2zrcy4ilz17eq" />
        </button>
        <button type="button" onClick={() => goTo(Math.min(last, active + 1))} disabled={active === last} aria-label="Página siguiente" className="hidden sm:flex absolute right-4 lg:right-8 top-[42%] -translate-y-1/2 w-11 h-11 rounded-full bg-white text-wine items-center justify-center shadow-lg transition-opacity duration-150 ease-out hover:bg-rose-50 disabled:opacity-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300" data-id="element-230" data-mp="2zps3yu3kjqhe">
          <ChevronRight className="w-5 h-5" data-id="element-231" data-mp="29bkj4a1ve2xv" />
        </button>
      </div>

      <div className="flex justify-center items-center gap-2 mt-6" role="tablist" aria-label="Elegí una página" data-id="element-232" data-mp="24hq6trolgq8t">
        {productPages.map((page, i) => <button key={page.src} type="button" role="tab" aria-selected={active === i} aria-label={`Ver ${page.tag}: ${page.title}`} onClick={() => goTo(i)} className="p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 rounded-full" data-id="element-233" data-mp="1ulmszokdr361" data-mpi={i}>
            <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-rose-500' : 'w-2 bg-celeste-300'}`} data-id="element-234" data-mp="1b164p1ks5jzo" />
          </button>)}
      </div>
    </section>;
}